import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Text, Button, Card, TextField, Callout, Spinner } from 'frosted-ui';
import { ArrowLeft16, ExclamationCircle16 } from '@frosted-ui/icons';
import { api } from '../services/api';
import { ApiRequestError } from '../services/apiErrors';
import type { Experiment, Placement } from '../types';
import { SPOTS, slugify, spotById, type SpotId } from '../utils/paths';
import { createPlacementWithCode } from '../utils/createPlacement';
import { SpotChips, SpotInstructions, ReplaceDontAdd } from '../components/wizard/StepPlaceLink';
import { cleanUsername, isValidUsername, USERNAME_RULE } from '../components/wizard/StepPlacePartners';
import LoadFailed from '../components/LoadFailed';
import { PlacementLinks } from '../components/wizard/FirstVisitChecklist';

type Kind = 'link' | 'partner';

/** Add one more spot (or partner link) to an existing test. */
export default function AddSpot() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [experiment, setExperiment] = useState<Experiment | null>(null);
  const [existing, setExisting] = useState<Placement[]>([]);
  const [kind, setKind] = useState<Kind>('link');
  const [spotId, setSpotId] = useState<SpotId>('bio');
  const [label, setLabel] = useState(spotById('bio').label);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The test itself didn't load (bad id, another business's): no form.
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [created, setCreated] = useState<Placement | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.getExperiment(id), api.getPlacements(id)])
      .then(([exp, placements]) => {
        if (cancelled) return;
        setExperiment(exp);
        setExisting(placements);
        // A partners test adds another partner by default.
        if (placements.length > 0 && placements.every((p) => p.kind === 'partner')) {
          setKind('partner');
          setLabel('');
          return;
        }
        // Preselect the first spot the test doesn't use yet.
        const used = new Set(placements.map((p) => p.label.toLowerCase()));
        const next = SPOTS.find((s) => s.id !== 'other' && !used.has(s.label.toLowerCase()));
        if (next) {
          setSpotId(next.id);
          setLabel(next.label);
        }
      })
      .catch((err: unknown) => {
        console.error('Failed to load the test:', err);
        if (cancelled) return;
        if (err instanceof ApiRequestError && (err.status === 403 || err.status === 404)) setNotFound(true);
        else setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, loadAttempt]);

  const trimmed = label.trim();
  const duplicate = existing.some((p) => p.label.toLowerCase() === trimmed.toLowerCase());
  const ready = Boolean(trimmed) && !duplicate && (kind === 'link' || isValidUsername(affiliateCode));

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const username = cleanUsername(affiliateCode);
      if (kind === 'partner' && username) {
        const exists = await api.checkUsername(experiment?.companyId ?? '', username).catch(() => null);
        if (exists === false) {
          setError(`Whop has no user called “${username}”. Check the spelling, or clear it: without a username the partner gets no credit for sales.`);
          return;
        }
      }
      const slug = slugify(trimmed);
      const placement =
        kind === 'link'
          ? await createPlacementWithCode(
              id,
              { kind: 'link', label: trimmed },
              spotId === 'other' ? slug || 'link' : spotById(spotId).code
            )
          : await createPlacementWithCode(
              id,
              {
                kind: 'partner',
                label: trimmed,
                ...(slug && { source: slug }),
                ...(username && { affiliateCode: username }),
              },
              slug || undefined
            );
      setCreated(placement);
    } catch (err) {
      setError((err as Error).message || "Couldn't add the link. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (notFound) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <Card size="3" className="vara2-step-in">
          <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
            We couldn't find that test
          </Heading>
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            It may have been deleted, or it belongs to another business.
          </Text>
          <Button size="3" variant="solid" onClick={() => navigate('/')}>
            Back to your tests
          </Button>
        </Card>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <LoadFailed
          what="this test"
          onRetry={() => {
            setLoadFailed(false);
            setLoadAttempt((n) => n + 1);
          }}
        />
      </div>
    );
  }

  if (!experiment) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Spinner size="3" />
      </div>
    );
  }

  // A finished test never splits buyers again, so a new spot has nothing to feed.
  if (experiment.archived || experiment.status === 'winner_declared') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <Card size="3" className="vara2-step-in">
          <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
            This test is over
          </Heading>
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            Only a test that's live, paused or still a draft can take a new link.
          </Text>
          <Button size="3" variant="solid" onClick={() => navigate(`/experiments/${id}`)}>
            Back to results
          </Button>
        </Card>
      </div>
    );
  }

  const unlock = experiment.offerType === 'unlock';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Button variant="ghost" color="gray" size="2" onClick={() => navigate(`/experiments/${id}`)}>
          <ArrowLeft16 />
          {experiment.name}
        </Button>
      </div>

      {created ? (
        <Card size="3" className="vara2-step-in">
          <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
            {created.label} added
          </Heading>
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            {experiment.status === 'running'
              ? 'It feeds the same test, and results show it as its own source.'
              : unlock
                ? "It feeds the same test once it's live. Until then it shows the offer as unavailable."
                : "It feeds the same test once it's live. Until then it lands on your regular store."}
          </Text>
          <PlacementLinks placements={[created]} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
            <Button size="3" variant="solid" onClick={() => navigate(`/experiments/${id}`)}>
              Back to results
            </Button>
            <Button
              size="3"
              variant="soft"
              color="gray"
              onClick={() => {
                setExisting((prev) => [...prev, created]);
                setCreated(null);
                setLabel('');
                setSpotId('other');
                setAffiliateCode('');
              }}
            >
              Add another
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div>
            <Heading size="6" style={{ marginBottom: 'var(--space-2)' }}>
              Add a link
            </Heading>
            <Text size="2" color="gray">
              More places, more buyers, a faster answer. Each place gets its own link; results
              combine and you can still see each one.
            </Text>
          </div>

          <div role="group" aria-label="Kind of link" style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {(['link', 'partner'] as const).map((k) => (
              <Button
                key={k}
                type="button"
                size="2"
                variant={kind === k ? 'solid' : 'surface'}
                color={kind === k ? undefined : 'gray'}
                aria-pressed={kind === k}
                onClick={() => {
                  setKind(k);
                  setLabel(k === 'link' && spotId !== 'other' ? spotById(spotId).label : '');
                }}
              >
                {k === 'link' ? 'A place I post' : 'A partner or ad'}
              </Button>
            ))}
          </div>

          {kind === 'link' && (
            <SpotChips
              value={spotId}
              onSelect={(sid) => {
                setSpotId(sid);
                setLabel(sid === 'other' ? '' : spotById(sid).label);
              }}
            />
          )}

          {(kind === 'partner' || spotId === 'other') && (
            <div>
              <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                {kind === 'partner' ? 'Partner or campaign' : 'Name this link'}
              </Text>
              <TextField.Root size="2" variant="surface">
                <TextField.Input
                  value={label}
                  maxLength={80}
                  placeholder={kind === 'partner' ? 'e.g. Mike (clipper), TikTok ads' : 'e.g. Newsletter footer'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
                />
              </TextField.Root>
            </div>
          )}

          {kind === 'partner' && (
            <div>
              <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                Their Whop username (optional)
              </Text>
              <TextField.Root size="2" variant="surface" color={isValidUsername(affiliateCode) ? undefined : 'red'}>
                <TextField.Input
                  value={affiliateCode}
                  maxLength={41}
                  placeholder="e.g. mike"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAffiliateCode(e.target.value)}
                />
              </TextField.Root>
              {!isValidUsername(affiliateCode) && (
                <Text size="1" color="red" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                  {USERNAME_RULE}
                </Text>
              )}
            </div>
          )}

          {duplicate && (
            <Text size="2" color="yellow">
              This test already has a link called “{trimmed}”. Pick another name.
            </Text>
          )}

          {kind === 'link' && !unlock && <SpotInstructions spotId={spotId} />}
          {kind === 'link' && !unlock && <ReplaceDontAdd />}

          {error && (
            <Callout.Root color="red">
              <Callout.Icon><ExclamationCircle16 /></Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </Callout.Root>
          )}

          <div>
            <Button size="3" variant="solid" onClick={handleCreate} disabled={!ready || saving} loading={saving}>
              Create link
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
